import type { CheckoutAgreementVersion } from '@iheartjane/data-access';

export interface KioskAgreementLink {
  label: string;
  url: string;
}

export interface KioskConsentItem {
  consentDefault: boolean;
  externalLink: string | null;
  externalLinks: KioskAgreementLink[];
  id: number;
  label: string;
  required: boolean;
}

const DEFAULT_LINK_LABEL = 'View Agreement';

/**
 * Maps the checkout-requirements agreement versions into the shape the kiosk
 * consent UI consumes. Reservation-mode and app_mode scoping are handled
 * server-side by Consent::Request, so every returned version is surfaced here.
 * Links with a blank url are dropped so they don't render a scannable-to-nothing
 * QR code.
 */
export const toKioskConsentItems = (
  versions: CheckoutAgreementVersion[] = []
): KioskConsentItem[] =>
  versions.map((version) => ({
    consentDefault: Boolean(version.consent_default),
    externalLink: version.external_link,
    externalLinks: (version.external_links ?? []).filter((link) =>
      link.url?.trim()
    ),
    id: version.id,
    label: version.label,
    required: version.consent_requirement.includes('required'),
  }));

/**
 * The links a shopper can open for an agreement, falling back to the legacy
 * single `external_link` when the versioned list is empty. Unlabeled links get
 * the default label so the kiosk always has something to render.
 */
export const toAgreementLinks = (
  item: KioskConsentItem
): KioskAgreementLink[] => {
  if (item.externalLinks.length > 0) {
    return item.externalLinks.map((link) => ({
      label: link.label || DEFAULT_LINK_LABEL,
      url: link.url,
    }));
  }

  return item.externalLink
    ? [{ label: DEFAULT_LINK_LABEL, url: item.externalLink }]
    : [];
};

/**
 * Caption for a link's QR page. Names the document the shopper is about to scan,
 * falling back to generic copy for an unlabeled link — "read View Agreement"
 * doesn't scan as a sentence.
 */
export const toAgreementScanPrompt = ({ label }: KioskAgreementLink): string =>
  label === DEFAULT_LINK_LABEL
    ? 'Scan here to read our checkout agreement'
    : `Scan here to read ${label}`;

/**
 * Effective consent for an item: the shopper's explicit choice if they've made
 * one, otherwise the agreement's consent_default (matching web, which seeds
 * checkboxes from consent_default). `??` so an explicit `false` is respected.
 */
export const isConsented = (
  item: KioskConsentItem,
  consents: Record<string, boolean>
): boolean => consents[String(item.id)] ?? item.consentDefault;

/** True when every required agreement has been consented to. */
export const areRequiredConsentsValid = (
  consentItems: KioskConsentItem[],
  consents: Record<string, boolean>
): boolean =>
  consentItems
    .filter((item) => item.required)
    .every((item) => isConsented(item, consents));

/** Builds the `user_consent` payload for the checkout submission. */
export const toUserConsentPayload = (
  consentItems: KioskConsentItem[],
  consents: Record<string, boolean>
): Array<{ checkout_agreement_version_id: number; consent: boolean }> =>
  consentItems.map((item) => ({
    checkout_agreement_version_id: item.id,
    consent: isConsented(item, consents),
  }));
